import { computed, reactive, ref, toValue, type MaybeRef } from 'vue'
import { useI18n } from 'vue-i18n'
import usePut from '@/core/composables/api/usePut'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { useSnackStore } from '@/stores/snack.store'
import {
  SurveyRecurrenceFrequency,
  serializeNextOccurrenceAt,
  type ErrorMap,
  type StartSurveyFormData,
} from '@/modules/start-survey/composables/use-start-survey'
import type { CoachSurvey } from './use-coach-survey'

type EditCoachSurveyFormData = StartSurveyFormData

export const useEditCoachSurvey = (survey: MaybeRef<CoachSurvey>, onClose: () => void) => {
  const { t } = useI18n()
  const snackStore = useSnackStore()

  const formData = reactive<EditCoachSurveyFormData>({
    title: undefined,
    message: undefined,
    feedbackTopicId: undefined,
    feedbackSenderIds: [],
    questionGroupIds: [],
    isRecurring: false,
    recurrenceFrequency: SurveyRecurrenceFrequency.Week,
    recurrenceInterval: 1,
    nextOccurrenceAt: tomorrowMorning(),
  })

  const errors = ref<ErrorMap>(new Map())

  const hasSurveyInstanceToday = computed(() => hasSurveyInstanceOnDay(toValue(survey), new Date()))

  const minNextOccurrenceAt = computed(() =>
    hasSurveyInstanceToday.value ? tomorrowMorning() : startOfDay(new Date()),
  )

  const validateForm = () => {
    errors.value.clear()

    if (formData.feedbackSenderIds.length === 0) {
      errors.value.set('feedbackSenderIds', t('feedback.formValidation.members'))
    }

    if (formData.nextOccurrenceAt) {
      const dateError = nextOccurrenceAtError(
        formData.nextOccurrenceAt,
        hasSurveyInstanceToday.value,
      )
      if (dateError) {
        errors.value.set('nextOccurrenceAt', t(dateError))
      }
    }

    if (formData.isRecurring) {
      if (!formData.nextOccurrenceAt) {
        errors.value.set('nextOccurrenceAt', t('feedback.formValidation.nextOccurrenceAt'))
      }
      if (
        !Number.isInteger(formData.recurrenceInterval) ||
        Number(formData.recurrenceInterval) < 1
      ) {
        errors.value.set('recurrenceInterval', t('feedback.formValidation.recurrenceInterval'))
      }

      if (
        formData.recurrenceFrequency === SurveyRecurrenceFrequency.Week &&
        ![1, 2, 4].includes(Number(formData.recurrenceInterval))
      ) {
        errors.value.set(
          'recurrenceInterval',
          t('feedback.formValidation.recurrenceWeeklyInterval'),
        )
      }
    }
  }

  const isFormValid = computed(() => {
    if (formData.feedbackSenderIds.length === 0) {
      return false
    }
    if (
      formData.nextOccurrenceAt &&
      nextOccurrenceAtError(formData.nextOccurrenceAt, hasSurveyInstanceToday.value)
    ) {
      return false
    }
    if (formData.isRecurring) {
      if (!formData.nextOccurrenceAt) {
        return false
      }
      if (
        !Number.isInteger(formData.recurrenceInterval) ||
        Number(formData.recurrenceInterval) < 1
      ) {
        return false
      }
    }
    return true
  })

  const getCurrentMemberIds = () => {
    return toValue(survey).surveyTemplateMembers.map((member) => member.id) ?? []
  }

  const resetForm = () => {
    const currentSurvey = toValue(survey)
    formData.title = currentSurvey.title ?? undefined
    formData.message = currentSurvey.message ?? undefined
    formData.feedbackSenderIds = getCurrentMemberIds()
    formData.recurrenceInterval = currentSurvey.recurrence?.interval ?? 1
    formData.recurrenceFrequency =
      currentSurvey.recurrence?.frequency ?? SurveyRecurrenceFrequency.Week
    formData.nextOccurrenceAt = nextOccurrenceAtFromSurvey(
      currentSurvey,
      hasSurveyInstanceToday.value,
    )
    formData.isRecurring = currentSurvey.isRecurring ?? false
    errors.value.clear()
  }

  resetForm()

  const { mutate: updateSurvey, status: updateSurveyStatus } = usePut<
    void,
    UpdateCoachSurveyPayload
  >(`coaching/surveys/${toValue(survey).id}`)

  const saveSurveyEdits = async () => {
    validateForm()
    if (!survey || errors.value.size > 0) {
      return
    }

    await updateSurvey(buildUpdatePayload(formData))

    if (updateSurveyStatus.value === ApiRequestStatus.Success) {
      await onClose()
      snackStore.showInfo('Umfrage wurde aktualisiert.')
      return
    }

    snackStore.showError('Umfrage konnte nicht aktualisiert werden.')
  }

  const isSaving = computed(() => updateSurveyStatus.value === ApiRequestStatus.Loading)

  return {
    errors,
    isFormValid,
    saveSurveyEdits,
    formData,
    resetForm,
    isSaving,
    minNextOccurrenceAt,
  }
}

const nextOccurrenceAtError = (
  date: Date,
  instanceToday: boolean,
):
  | 'feedback.formValidation.fixedNextDate'
  | 'feedback.formValidation.nextOccurrenceAtOccupied'
  | null => {
  if (!notInThePast(date)) {
    return 'feedback.formValidation.fixedNextDate'
  }
  if (instanceToday && isSameDay(date, new Date())) {
    return 'feedback.formValidation.nextOccurrenceAtOccupied'
  }
  return null
}

const nextOccurrenceAtFromSurvey = (
  survey: CoachSurvey,
  instanceToday: boolean,
): Date | undefined => {
  const nextOccurrenceAt = survey.nextOccurrenceAt ?? survey.recurrence?.nextOccurrenceAt
  if (!nextOccurrenceAt) {
    return undefined
  }
  const nextDate = new Date(nextOccurrenceAt)
  if (nextOccurrenceAtError(nextDate, instanceToday)) {
    return tomorrowMorning()
  }
  return nextDate
}

const hasSurveyInstanceOnDay = (survey: CoachSurvey, date: Date) =>
  survey.analysis.surveys.some((occurrence) => isSameDay(new Date(occurrence.date), date))

const tomorrowMorning = () => {
  const date = new Date()
  date.setDate(date.getDate() + 1)
  date.setHours(9, 0, 0, 0)
  return date
}

const startOfDay = (date: Date) => {
  const result = new Date(date)
  result.setHours(0, 0, 0, 0)
  return result
}

const isSameDay = (left: Date, right: Date) =>
  startOfDay(left).getTime() === startOfDay(right).getTime()

const notInThePast = (date: Date) => startOfDay(date).getTime() >= startOfDay(new Date()).getTime()

const buildUpdatePayload = (formData: EditCoachSurveyFormData): UpdateCoachSurveyPayload => {
  const base = {
    title: formData.title,
    message: formData.message,
    feedbackSenderIds: formData.feedbackSenderIds,
  }

  if (formData.isRecurring && formData.nextOccurrenceAt) {
    return {
      ...base,
      isRecurring: true,
      recurrenceFrequency: formData.recurrenceFrequency,
      recurrenceInterval: formData.recurrenceInterval,
      nextOccurrenceAt: serializeNextOccurrenceAt(formData.nextOccurrenceAt),
    }
  }

  if (formData.nextOccurrenceAt) {
    return {
      ...base,
      isRecurring: false,
      nextOccurrenceAt: serializeNextOccurrenceAt(formData.nextOccurrenceAt, {
        mustBeFuture: true,
      }),
    }
  }

  return {
    ...base,
    isRecurring: false,
    nextOccurrenceAt: null,
  }
}

type UpdateCoachSurveyPayload = {
  title?: string
  message?: StartSurveyFormData['message']
  feedbackSenderIds: string[]
  isRecurring?: boolean
  recurrenceFrequency?: SurveyRecurrenceFrequency
  recurrenceInterval?: number
  nextOccurrenceAt?: string | null
}
